import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { useStore } from '../../store';
import { files as filesApi } from '../../api';
import Avatar from '../UI/Avatar';
import {
  Upload, Download, Trash2, Eye, FileText, FileSpreadsheet,
  File, Image as ImageIcon, X, Search, Plus, AlertCircle,
  CheckCircle2, Loader2, ChevronLeft, ChevronRight,
  ZoomIn, ZoomOut, RotateCw, FileX, Info
} from 'lucide-react';
import { fmt } from '../../utils';

// ── Constants ────────────────────────────────────────────────────────────────

const FILE_ICONS = {
  pdf: { icon: FileText, color: 'text-red-500', bg: 'bg-red-50 dark:bg-red-950/40', label: 'PDF' },
  doc: { icon: FileText, color: 'text-blue-500', bg: 'bg-blue-50 dark:bg-blue-950/40', label: 'Документ' },
  spreadsheet: { icon: FileSpreadsheet, color: 'text-green-500', bg: 'bg-green-50 dark:bg-green-950/40', label: 'Таблица' },
  image: { icon: ImageIcon, color: 'text-purple-500', bg: 'bg-purple-50 dark:bg-purple-950/40', label: 'Изображение' },
  other: { icon: File, color: 'text-gray-500', bg: 'bg-gray-50 dark:bg-gray-800', label: 'Файл' },
};



function fmtSize(bytes) {
  if (!bytes) return '—';
  if (bytes < 1024) return `${bytes} Б`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} КБ`;
  return `${(bytes / 1024 / 1024).toFixed(1)} МБ`;
}

function canPreview(file) {
  return file.category === 'pdf' || file.category === 'image' || file.category === 'doc' || file.category === 'spreadsheet';
}

function canPreviewAsPdf(file) {
  return file.category === 'doc' || file.category === 'spreadsheet';
}

// ── Full-screen Lightbox viewer ───────────────────────────────────────────────

function Lightbox({ files, currentIndex, onClose, onIndexChange, onDelete }) {
  const file = files[currentIndex];
  const [imgZoom, setImgZoom] = useState(1);
  const [imgRotate, setImgRotate] = useState(0);
  const [showInfo, setShowInfo] = useState(false);
  const [loading, setLoading] = useState(true);

  const previewBase = filesApi.preview(file._id);
  const previewUrl = canPreviewAsPdf(file) ? `${previewBase}&format=pdf` : previewBase;
  const downloadUrl = filesApi.download(file._id);
  const meta = FILE_ICONS[file.category] || FILE_ICONS.other;
  const Icon = meta.icon;
  console.log('Preview URL', previewUrl);

  // Reset zoom/rotate on file change
  useEffect(() => {
    setImgZoom(1);
    setImgRotate(0);
    setLoading(canPreview(file));
  }, [currentIndex, file]);

  // Keyboard navigation
  useEffect(() => {
    const handler = e => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft' && currentIndex > 0) onIndexChange(currentIndex - 1);
      if (e.key === 'ArrowRight' && currentIndex < files.length - 1) onIndexChange(currentIndex + 1);
      if (e.key === '+' || e.key === '=') setImgZoom(z => Math.min(z + 0.25, 4));
      if (e.key === '-') setImgZoom(z => Math.max(z - 0.25, 0.25));
      if (e.key === 'r') setImgRotate(r => r + 90);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [currentIndex, files.length]);

  const handleDelete = async () => {
    if (!window.confirm(`Удалить «${file.originalName}»?`)) return;
    await onDelete(file._id);
    if (files.length === 1) { onClose(); return; }
    onIndexChange(currentIndex > 0 ? currentIndex - 1 : 0);
  };

  return (
    <div className="fixed inset-0 z-50 flex bg-black/95" style={{ userSelect: 'none' }}>

      {/* ── Left: nav ──────────────────────────────────────────────────────── */}
      <button
        onClick={() => onIndexChange(currentIndex - 1)}
        disabled={currentIndex === 0}
        className="absolute left-2 top-1/2 -translate-y-1/2 z-20 p-2 rounded-full bg-white/10 hover:bg-white/20 text-white disabled:opacity-20 disabled:cursor-not-allowed transition-all"
        style={{ marginTop: '28px' }}
      >
        <ChevronLeft className="w-6 h-6" />
      </button>

      {/* ── Right: nav ─────────────────────────────────────────────────────── */}
      <button
        onClick={() => onIndexChange(currentIndex + 1)}
        disabled={currentIndex === files.length - 1}
        className="absolute right-2 sm:right-64 top-1/2 -translate-y-1/2 z-20 p-2 rounded-full bg-white/10 hover:bg-white/20 text-white disabled:opacity-20 disabled:cursor-not-allowed transition-all"
        style={{ marginTop: '28px' }}
      >
        <ChevronRight className="w-6 h-6" />
      </button>

      {/* ── Top bar ────────────────────────────────────────────────────────── */}
      <div className="absolute top-0 left-0 right-0 z-20 flex items-center gap-2 px-4 py-3 bg-gradient-to-b from-black/80 to-transparent">
        <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-white/10 text-gray-300 hover:text-white transition-colors flex-shrink-0">
          <X className="w-5 h-5" />
        </button>
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-medium text-white truncate">{file.originalName}</h3>
          <p className="text-[11px] text-gray-400">{currentIndex + 1} из {files.length} · {fmtSize(file.size)}</p>
        </div>

        {/* Toolbar for images */}
        {file.category === 'image' && (
          <div className="hidden sm:flex items-center gap-1 bg-white/10 rounded-lg px-1 py-1">
            <button onClick={() => setImgZoom(z => Math.max(z - 0.25, 0.25))} className="p-1.5 hover:bg-white/10 rounded text-gray-300 hover:text-white transition-colors" title="Уменьшить (-)">
              <ZoomOut className="w-4 h-4" />
            </button>
            <span className="text-xs text-gray-400 w-10 text-center">{Math.round(imgZoom * 100)}%</span>
            <button onClick={() => setImgZoom(z => Math.min(z + 0.25, 4))} className="p-1.5 hover:bg-white/10 rounded text-gray-300 hover:text-white transition-colors" title="Увеличить (+)">
              <ZoomIn className="w-4 h-4" />
            </button>
            <button onClick={() => { setImgZoom(1); setImgRotate(0); }} className="p-1.5 hover:bg-white/10 rounded text-xs text-gray-400 hover:text-white transition-colors px-2">
              1:1
            </button>
            <button onClick={() => setImgRotate(r => r + 90)} className="p-1.5 hover:bg-white/10 rounded text-gray-300 hover:text-white transition-colors" title="Повернуть (R)">
              <RotateCw className="w-4 h-4" />
            </button>
          </div>
        )}

        <button onClick={() => setShowInfo(s => !s)} className={`p-1.5 rounded-lg hover:bg-white/10 transition-colors ${showInfo ? 'text-brand-400 bg-white/10' : 'text-gray-400 hover:text-white'}`} title="Информация">
          <Info className="w-4 h-4" />
        </button>
        <a href={downloadUrl} download={file.originalName}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-brand-600 hover:bg-brand-500 text-white text-xs font-medium transition-colors">
          <Download className="w-3.5 h-3.5" /><span className="hidden sm:inline">Скачать</span>
        </a>
        <button onClick={handleDelete} className="p-1.5 rounded-lg hover:bg-red-500/20 text-gray-400 hover:text-red-400 transition-colors">
          <Trash2 className="w-4 h-4" />
        </button>
      </div>

      {/* ── Main preview area ──────────────────────────────────────────────── */}
      <div className={`flex-1 flex items-center justify-center overflow-hidden transition-all ${showInfo ? 'mr-0 sm:mr-64' : ''}`}
        style={{ paddingTop: 60, paddingBottom: 80 }}>

        {(file.category === 'pdf' || canPreviewAsPdf(file)) && (
          <iframe
            key={file._id}
            src={`${previewUrl}#toolbar=1&navpanes=0&view=FitH`}
            className="w-full h-full rounded-none sm:rounded-lg shadow-2xl bg-white"
            style={{ maxWidth: showInfo ? 'calc(100vw - 256px - 48px)' : 960, border: 'none' }}
            title={file.originalName}
            onLoad={() => setLoading(false)}
          />
        )}

        {file.category === 'image' && (
          <div className="flex items-center justify-center w-full h-full overflow-auto p-4">
            <img
              key={file._id}
              src={previewUrl}
              alt={file.originalName}
              onLoad={() => setLoading(false)}
              className="rounded-lg shadow-2xl object-contain transition-transform duration-200 cursor-zoom-in"
              style={{
                transform: `scale(${imgZoom}) rotate(${imgRotate}deg)`,
                maxWidth: imgZoom === 1 ? '100%' : 'none',
                maxHeight: imgZoom === 1 ? '100%' : 'none',
              }}
              onClick={() => setImgZoom(z => z >= 2 ? 1 : z + 0.5)}
              draggable={false}
            />
          </div>
        )}

        {/* Non-previewable types */}
        {!canPreview(file) && (
          <div className="flex flex-col items-center gap-4 text-center p-8">
            <div className={`w-24 h-24 rounded-2xl flex items-center justify-center ${meta.bg}`}>
              <Icon className={`w-12 h-12 ${meta.color}`} />
            </div>
            <div>
              <p className="text-white font-semibold text-lg">{file.originalName}</p>
              <p className="text-gray-400 text-sm mt-1">{meta.label} · {fmtSize(file.size)}</p>
            </div>
            <p className="text-gray-500 text-sm">Предпросмотр недоступен для этого типа файла</p>
            <a href={downloadUrl} download={file.originalName}
              className="btn-primary mt-2">
              <Download className="w-4 h-4" /> Скачать файл
            </a>
          </div>
        )}

        {/* Loading spinner */}
        {loading && canPreview(file) && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none" style={{ paddingTop: 60 }}>
            <Loader2 className="w-8 h-8 text-white/40 animate-spin" />
          </div>
        )}
      </div>

      {/* ── Info panel (right side) ────────────────────────────────────────── */}
      {showInfo && (
        <div className="absolute right-0 top-0 bottom-0 w-full sm:w-64 bg-gray-900/95 border-l border-white/10 flex flex-col z-10 overflow-y-auto"
          style={{ paddingTop: 60, paddingBottom: 80 }}>
          <div className="p-4 space-y-4">
            <div>
              <p className="text-[11px] text-gray-500 uppercase tracking-wider mb-1">Файл</p>
              <p className="text-sm text-white font-medium break-all">{file.originalName}</p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <p className="text-[11px] text-gray-500 mb-0.5">Тип</p>
                <span className={`tag text-[11px] ${meta.bg} ${meta.color}`}>{meta.label}</span>
              </div>
              <div>
                <p className="text-[11px] text-gray-500 mb-0.5">Размер</p>
                <p className="text-xs text-gray-300">{fmtSize(file.size)}</p>
              </div>
            </div>
            <div>
              <p className="text-[11px] text-gray-500 mb-0.5">Загружен</p>
              <p className="text-xs text-gray-300">{fmt.date(file.createdAt)}</p>
              <p className="text-[11px] text-gray-500 mt-0.5">{fmt.ago(file.createdAt)}</p>
            </div>
            {file.uploadedBy && (
              <div>
                <p className="text-[11px] text-gray-500 mb-1.5">Загрузил</p>
                <div className="flex items-center gap-2">
                  <Avatar name={file.uploadedBy.name} color={file.uploadedBy.color} src={file.uploadedBy.avatar} size="sm" />
                  <div>
                    <p className="text-xs text-white font-medium">{file.uploadedBy.name}</p>
                    <p className="text-[11px] text-gray-500">{file.uploadedBy.email}</p>
                  </div>
                </div>
              </div>
            )}
            {file.description && (
              <div>
                <p className="text-[11px] text-gray-500 mb-1">Описание</p>
                <p className="text-xs text-gray-300">{file.description}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Bottom filmstrip ───────────────────────────────────────────────── */}
      <div className="absolute bottom-0 left-0 right-0 z-20 flex items-center gap-2 px-4 py-3 bg-gradient-to-t from-black/80 to-transparent overflow-x-auto">
        <div className="flex gap-2 mx-auto">
          {files.map((f, i) => {
            const fm = FILE_ICONS[f.category] || FILE_ICONS.other;
            const FmIcon = fm.icon;
            const isActive = i === currentIndex;
            return (
              <button key={f._id} onClick={() => onIndexChange(i)}
                className={`flex-shrink-0 w-12 h-12 sm:w-14 sm:h-14 rounded-lg overflow-hidden border-2 transition-all ${isActive ? 'border-brand-400 scale-110' : 'border-white/20 opacity-60 hover:opacity-100'}`}>
                {f.category === 'image' ? (
                  <img src={filesApi.preview(f._id)} alt={f.originalName} className="w-full h-full object-cover" />
                ) : (
                  <div className={`w-full h-full flex items-center justify-center ${fm.bg}`}>
                    <FmIcon className={`w-5 h-5 ${fm.color}`} />
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Keyboard hint */}
      <div className="absolute bottom-20 left-1/2 -translate-x-1/2 text-[10px] text-white/20 hidden sm:flex gap-3">
        <span>← → навигация</span>
        <span>+/- зум</span>
        <span>R поворот</span>
        <span>Esc закрыть</span>
      </div>
    </div>
  );
}

// ── Upload Drop Zone ─────────────────────────────────────────────────────────

function UploadZone({ projectId, onUploaded }) {
  const [dragging, setDragging] = useState(false);
  const [uploads, setUploads] = useState([]);
  const inputRef = useRef();

  const processFiles = useCallback(async (fileList) => {
    const arr = Array.from(fileList);
    const entries = arr.map(f => ({ id: Math.random(), name: f.name, progress: 0, status: 'uploading', error: null }));
    setUploads(prev => [...prev, ...entries]);

    for (let i = 0; i < arr.length; i++) {
      const f = arr[i];
      const entry = entries[i];
      const fd = new FormData();
      fd.append('file', f);
      fd.append('project', projectId);
      try {
        const result = await filesApi.upload(projectId, fd, e => {
          const pct = Math.round((e.loaded / e.total) * 100);
          setUploads(prev => prev.map(u => u.id === entry.id ? { ...u, progress: pct } : u));
        });
        setUploads(prev => prev.map(u => u.id === entry.id ? { ...u, status: 'done', progress: 100 } : u));
        onUploaded(result);
      } catch (err) {
        setUploads(prev => prev.map(u => u.id === entry.id ? { ...u, status: 'error', error: err?.message || 'Ошибка' } : u));
      }
    }
    setTimeout(() => setUploads(prev => prev.filter(u => u.status !== 'done')), 3000);
  }, [projectId, onUploaded]);

  return (
    <div className="mb-5">
      <div
        onDrop={e => { e.preventDefault(); setDragging(false); processFiles(e.dataTransfer.files); }}
        onDragOver={e => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        className={`border-2 border-dashed rounded-xl transition-all ${dragging ? 'border-brand-400 bg-brand-50 dark:bg-brand-900/20' : 'border-gray-200 dark:border-gray-700 hover:border-brand-300 dark:hover:border-brand-700 bg-gray-50 dark:bg-gray-800/30'}`}
      >
        <input ref={inputRef} type="file" multiple className="hidden"
          accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.ppt,.pptx,.jpg,.jpeg,.png,.gif,.webp,.svg"
          onChange={e => processFiles(e.target.files)} />
        <div className="flex flex-col sm:flex-row items-center gap-3 sm:gap-4 p-4 sm:p-5">
          <Upload className={`w-7 h-7 flex-shrink-0 transition-colors ${dragging ? 'text-brand-500' : 'text-gray-300 dark:text-gray-600'}`} />
          <div className="flex-1 text-center sm:text-left">
            <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
              {dragging ? 'Отпустите для загрузки' : 'Перетащите файлы сюда'}
            </p>
            <p className="text-xs text-gray-400 mt-0.5">PDF, Word, Excel, PowerPoint, изображения · до 50 МБ</p>
          </div>
          <button type="button" onClick={() => inputRef.current?.click()}
            className="btn-primary py-2 px-4 flex-shrink-0 w-full sm:w-auto justify-center">
            <Plus className="w-4 h-4" /> Выбрать файлы
          </button>
        </div>
      </div>

      {uploads.length > 0 && (
        <div className="mt-3 space-y-2">
          {uploads.map(u => (
            <div key={u.id} className="flex items-center gap-3 px-3 py-2 rounded-lg bg-gray-50 dark:bg-gray-800">
              {u.status === 'uploading' && <Loader2 className="w-4 h-4 text-brand-500 animate-spin flex-shrink-0" />}
              {u.status === 'done' && <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0" />}
              {u.status === 'error' && <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0" />}
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium truncate">{u.name}</p>
                {u.status === 'uploading' && (
                  <div className="mt-1 h-1 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                    <div className="h-full bg-brand-500 rounded-full transition-all" style={{ width: `${u.progress}%` }} />
                  </div>
                )}
                {u.status === 'error' && <p className="text-[11px] text-red-500">{u.error}</p>}
              </div>
              {u.status === 'uploading' && <span className="text-[11px] text-gray-400 flex-shrink-0">{u.progress}%</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── File Card (grid) ──────────────────────────────────────────────────────────

function FileCard({ file, index, onOpen, onDelete }) {
  const meta = FILE_ICONS[file.category] || FILE_ICONS.other;
  const Icon = meta.icon;
  const previewBase = filesApi.preview(file._id);
  const previewUrl = canPreviewAsPdf(file) ? `${previewBase}?format=pdf` : previewBase;
  const downloadUrl = filesApi.download(file._id);

  return (
    <div className="card flex flex-col group hover:shadow-md transition-all duration-200 overflow-hidden cursor-pointer"
      onClick={() => onOpen(index)}>
      {/* Thumbnail */}
      <div className={`w-full aspect-video flex items-center justify-center overflow-hidden ${meta.bg} relative`}>
        {file.category === 'image' ? (
          <img src={previewUrl} alt={file.originalName}
            className="w-full h-full object-cover" loading="lazy" />
        ) : (
          <Icon className={`w-10 h-10 ${meta.color}`} />
        )}
        {/* Open overlay */}
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all flex items-center justify-center">
          <Eye className="w-6 h-6 text-white opacity-0 group-hover:opacity-100 transition-all scale-75 group-hover:scale-100" />
        </div>
      </div>

      {/* Info */}
      <div className="p-3 flex-1 flex flex-col gap-1.5 min-w-0">
        <p className="text-xs font-medium truncate" title={file.originalName}>{file.originalName}</p>
        <div className="flex items-center gap-1.5">
          <span className={`tag text-[10px] ${meta.bg} ${meta.color}`}>{meta.label}</span>
          <span className="text-[10px] text-gray-400">{fmtSize(file.size)}</span>
        </div>
        {file.description && (
          <p className="text-[11px] text-gray-400 line-clamp-1">{file.description}</p>
        )}
      </div>

      {/* Footer */}
      <div className="px-3 pb-3 flex items-center gap-1.5 border-t border-gray-100 dark:border-gray-800 pt-2">
        <Avatar name={file.uploadedBy?.name} color={file.uploadedBy?.color} src={file.uploadedBy?.avatar} size="xs" />
        <span className="text-[10px] text-gray-400 flex-1 truncate">{fmt.ago(file.createdAt)}</span>
        {/* Quick actions — stop propagation so they don't open lightbox */}
        <div className="flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
          <a href={downloadUrl} download={file.originalName}
            onClick={e => e.stopPropagation()}
            className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-400 hover:text-emerald-600 transition-colors">
            <Download className="w-3.5 h-3.5" />
          </a>
          <button onClick={e => { e.stopPropagation(); onDelete(file._id); }}
            className="p-1 rounded hover:bg-red-50 dark:hover:bg-red-950/30 text-gray-400 hover:text-red-500 transition-colors">
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
}

// ── File Row (list) ───────────────────────────────────────────────────────────

function FileRow({ file, index, onOpen, onDelete }) {
  const meta = FILE_ICONS[file.category] || FILE_ICONS.other;
  const Icon = meta.icon;
  const previewBase = filesApi.preview(file._id);
  const previewUrl = canPreviewAsPdf(file) ? `${previewBase}?format=pdf` : previewBase;
  const downloadUrl = filesApi.download(file._id);

  return (
    <tr className="hover:bg-gray-50 dark:hover:bg-gray-800/50 group transition-colors cursor-pointer"
      onClick={() => onOpen(index)}>
      <td className="px-4 py-3">
        <div className="flex items-center gap-3">
          <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${meta.bg} group-hover:scale-105 transition-transform`}>
            <Icon className={`w-4 h-4 ${meta.color}`} />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-medium truncate max-w-xs group-hover:text-brand-600 dark:group-hover:text-brand-400 transition-colors">{file.originalName}</p>
            {file.description && <p className="text-xs text-gray-400 truncate max-w-xs">{file.description}</p>}
          </div>
        </div>
      </td>
      <td className="px-3 py-3 hidden sm:table-cell">
        <span className={`tag text-[11px] ${meta.bg} ${meta.color}`}>{meta.label}</span>
      </td>
      <td className="px-3 py-3 text-xs text-gray-500 hidden md:table-cell">{fmtSize(file.size)}</td>
      <td className="px-3 py-3 hidden lg:table-cell">
        <div className="flex items-center gap-1.5">
          <Avatar name={file.uploadedBy?.name} color={file.uploadedBy?.color} src={file.uploadedBy?.avatar} size="xs" />
          <span className="text-xs text-gray-500 truncate max-w-[100px]">{file.uploadedBy?.name}</span>
        </div>
      </td>
      <td className="px-3 py-3 text-xs text-gray-400 hidden md:table-cell">{fmt.date(file.createdAt)}</td>
      <td className="px-3 py-3" onClick={e => e.stopPropagation()}>
        <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity justify-end">
          <button onClick={() => onOpen(index)}
            className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-400 hover:text-brand-600 transition-colors" title="Открыть просмотр">
            <Eye className="w-4 h-4" />
          </button>
          <a href={downloadUrl} download={file.originalName}
            className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-400 hover:text-emerald-600 transition-colors">
            <Download className="w-4 h-4" />
          </a>
          <button onClick={() => onDelete(file._id)}
            className="p-1.5 rounded hover:bg-red-50 dark:hover:bg-red-950/40 text-gray-400 hover:text-red-500 transition-colors">
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </td>
    </tr>
  );
}

// ── Main FilesView ────────────────────────────────────────────────────────────

export default function FilesView() {
  const { activeProject } = useStore();
  const [fileList, setFileList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lightboxIndex, setLightboxIndex] = useState(null); // null = closed
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [viewMode, setViewMode] = useState('grid');

  const fetchFiles = useCallback(async () => {
    if (!activeProject) return;
    setLoading(true);
    try { setFileList(await filesApi.list(activeProject._id)); }
    catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [activeProject]);

  useEffect(() => { fetchFiles(); }, [fetchFiles]);

  const handleUploaded = f => setFileList(prev => [f, ...prev]);

  const handleDelete = async (id) => {
    if (!window.confirm('Удалить файл?')) return;
    try {
      await filesApi.delete(id);
      setFileList(prev => prev.filter(f => f._id !== id));
    } catch (e) { alert(e.message); }
  };

  const filtered = useMemo(() => fileList.filter(f => {
    if (categoryFilter !== 'all' && f.category !== categoryFilter) return false;
    if (search && !f.originalName.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  }), [fileList, categoryFilter, search]);

  const stats = useMemo(() => fileList.reduce((acc, f) => {
    acc[f.category] = (acc[f.category] || 0) + 1;
    acc.total = (acc.total || 0) + 1;
    acc.size = (acc.size || 0) + f.size;
    return acc;
  }, {}), [fileList]);

  if (!activeProject) return null;

  return (
    <div className="h-full overflow-y-auto p-4 sm:p-5">
      <div className="max-w-6xl mx-auto">

        {/* Stats chips */}
        {fileList.length > 0 && (
          <div className="flex items-center gap-3 mb-4 flex-wrap">
            <span className="text-xs text-gray-400">{stats.total} файлов · {fmtSize(stats.size || 0)}</span>
            <div className="flex gap-1.5 flex-wrap">
              {Object.entries(FILE_ICONS).map(([cat, meta]) => stats[cat] ? (
                <button key={cat} onClick={() => setCategoryFilter(categoryFilter === cat ? 'all' : cat)}
                  className={`flex items-center gap-1 tag cursor-pointer transition-all ${categoryFilter === cat ? `${meta.bg} ${meta.color} ring-1 ring-current` : 'bg-gray-100 dark:bg-gray-800 text-gray-500 hover:bg-gray-200 dark:hover:bg-gray-700'}`}>
                  <meta.icon className="w-3 h-3" />{meta.label} {stats[cat]}
                </button>
              ) : null)}
            </div>
          </div>
        )}

        {/* Upload zone */}
        <UploadZone projectId={activeProject._id} onUploaded={handleUploaded} />

        {/* Toolbar */}
        {fileList.length > 0 && (
          <div className="flex items-center gap-2 mb-4 flex-wrap">
            <div className="relative flex-1 min-w-36">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
              <input className="input pl-8 text-sm" placeholder="Поиск…"
                value={search} onChange={e => setSearch(e.target.value)} />
            </div>
            <div className="flex gap-0.5 bg-gray-100 dark:bg-gray-800 rounded-lg p-0.5">
              {[['grid', '⊞', 'Плитки'], ['list', '☰', 'Список']].map(([mode, icon, lbl]) => (
                <button key={mode} onClick={() => setViewMode(mode)}
                  className={`px-2 sm:px-3 py-1.5 rounded-md text-xs font-medium transition-all ${viewMode === mode ? 'bg-white dark:bg-gray-700 shadow-sm' : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-300'}`}>
                  {icon} <span className="hidden sm:inline">{lbl}</span>
                </button>
              ))}
            </div>
            <span className="text-xs text-gray-400">{filtered.length} из {fileList.length}</span>
          </div>
        )}

        {/* Content */}
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-6 h-6 animate-spin text-brand-500" />
          </div>
        ) : filtered.length === 0 && fileList.length === 0 ? (
          <div className="text-center py-12 text-gray-400">
            <File className="w-12 h-12 mx-auto mb-3 opacity-30" />
            <p className="text-sm font-medium mb-1">Файлов нет</p>
            <p className="text-xs">Нажмите «Выбрать файлы» или перетащите в зону выше</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-10 text-gray-400 text-sm">Ничего не найдено</div>
        ) : viewMode === 'grid' ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 sm:gap-4">
            {filtered.map((f, i) => (
              <FileCard key={f._id} file={f} index={i} onOpen={setLightboxIndex} onDelete={handleDelete} />
            ))}
          </div>
        ) : (
          <div className="card overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 dark:border-gray-800">
                  <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-400">Файл</th>
                  <th className="text-left px-3 py-2.5 text-xs font-medium text-gray-400 w-28 hidden sm:table-cell">Тип</th>
                  <th className="text-left px-3 py-2.5 text-xs font-medium text-gray-400 w-20 hidden md:table-cell">Размер</th>
                  <th className="text-left px-3 py-2.5 text-xs font-medium text-gray-400 w-36 hidden lg:table-cell">Загрузил</th>
                  <th className="text-left px-3 py-2.5 text-xs font-medium text-gray-400 w-28 hidden md:table-cell">Дата</th>
                  <th className="w-28" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50 dark:divide-gray-800/50">
                {filtered.map((f, i) => (
                  <FileRow key={f._id} file={f} index={i} onOpen={setLightboxIndex} onDelete={handleDelete} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Lightbox */}
      {lightboxIndex !== null && filtered.length > 0 && (
        <Lightbox
          files={filtered}
          currentIndex={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
          onIndexChange={setLightboxIndex}
          onDelete={async (id) => {
            await filesApi.delete(id);
            setFileList(prev => prev.filter(f => f._id !== id));
          }}
        />
      )}
    </div>
  );
}
